import React from 'react';
import { ListColumnType, ListFilterType, QueryParamType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import { renderDate, renderPriceDecimalColumn, renderStatusBadge } from 'components/lists/functions/renderListColumn';
import { LocationType, PersonType } from '../../views/contacts/ContactTypes';
import InvoiceModal from './InvoiceModal';
import { invoiceFilters } from './InvoiceList';
import { useSettings } from 'services/settings/SettingsContext';
import { useActiveFeature } from 'services/permissions/permissionChecks';
import InvoiceTemplateDetailList from './InvoiceTemplateDetailList';

interface InvoiceDetailListProps {
    itemId: string;
    detailPage: string;
    data?: any;
}

const invoiceDetailListConfigurations: Record<string, { params: (itemId: string) => QueryParamType, showAddButton?: boolean, showSearchAndFilters?: boolean, showPeriodicInvoices?: boolean }> = {
    contact: { params: (itemId: string) => ({ 'recipient': itemId }), showPeriodicInvoices: true },
    organization: { params: (itemId: string) => ({ 'recipient': itemId }), showPeriodicInvoices: true },
    person: { params: (itemId: string) => ({ 'recipient': itemId }), showPeriodicInvoices: true },
    location: { params: (itemId: string) => ({ 'invoice_address': itemId }) },
    contract: { params: (itemId: string) => ({ 'contract': itemId, 'deleted': false }), showAddButton: false, showSearchAndFilters: false },
    project: { params: (itemId: string) => ({ 'project': itemId, 'deleted': false }), showAddButton: false, showSearchAndFilters: false },
};

const InvoiceDetailList: React.FC<InvoiceDetailListProps> = ({ itemId, detailPage, data }) => {
    const { userLocale } = useSettings();
    const periodicInvoicesActive = useActiveFeature('periodic_invoices');

    // Get the detail list configuration
    const configuration = invoiceDetailListConfigurations[detailPage];
    const defaultParams = configuration.params(itemId);

    // Determine whether the periodic invoices of the contact are shown above the invoices
    const showPeriodicInvoices = periodicInvoicesActive && configuration.showPeriodicInvoices === true;

    // Specify the columns of this list
    const columns: ListColumnType[] = [
        { 
            width: '1fr', 
            field: 'number', 
            label: 'invoice.general.invoice_number_label' 
        },
        { 
            width: '2fr', 
            field: 'internal_reference', 
            label: 'invoice.general.internal_reference_label' 
        },
        { 
            width: '1fr', 
            field: 'invoice_date', 
            label: 'invoice.general.date_label', 
            render: (_, row) => renderDate(row.invoice_date, userLocale) 
        },
        { 
            width: '1fr', 
            field: 'amount', 
            label: 'invoice.general.amount_label', 
            render: (_, row) => renderPriceDecimalColumn(row.amount, row.currency, userLocale) 
        },
        { 
            width: '144px', 
            field: 'status', 
            label: 'invoice.general.status_label', 
            disableOrdering: true,
            render: (_, row) => renderStatusBadge(row.status) 
        }
    ];

    // Add the page params to the default list filters
    const applyPageParamsToFilters = (filters: ListFilterType[], pageParams: QueryParamType) => {
        return filters.map(filter => {
            if ('filters' in filter) {
                filter.filters = filter.filters.map(subFilter => ({
                    ...subFilter,
                    params: { ...subFilter.params, ...pageParams }
                }));
            } else {
                filter.params = { ...(filter.params || {}), ...pageParams };
            }
            return filter;
        });
    };

    // Configure post new item object
    const postNewItem = (detailPage: string) => {
        let postDataConfig: { url: string; linkedItem?: Record<string, string | number>} = { url: 'post_invoice' };
        let linkedItem: Record<string, string | number> | undefined;

        switch (detailPage) {
            case 'contact':
                // Get the primary location of the contact
                const contactPrimaryLocations = data.primary_locations?.filter((location: LocationType) => !location.deleted) || [];

                // Get the work locations of the contact
                const contactWorkLocations = data.work_locations?.filter((location: LocationType) => !location.deleted) || [];

                // Get the primary persons of the contact
                const contactPrimaryPersons = data.primary_persons?.filter((person: PersonType) => !person.deleted) || [];

                linkedItem = { 
                    'recipient': itemId,
                    'recipient_person': contactPrimaryPersons.length === 1 ? contactPrimaryPersons[0].contact : null,
                    'invoice_address': contactPrimaryLocations.length === 1 && contactWorkLocations.length === 0 ? contactPrimaryLocations[0].id : null,
                };
                break;
            case 'organization':
                // Get the primary locations of the organization
                const organizationPrimaryLocations = data.primary_locations?.filter((location: LocationType) => !location.deleted) || [];

                // Get the primary persons of the organization
                const organizationPrimaryPersons = data.primary_persons?.filter((person: PersonType) => !person.deleted) || [];

                linkedItem = { 
                    'recipient': itemId,
                    'recipient_person': organizationPrimaryPersons.length === 1 ? organizationPrimaryPersons[0].contact : null,
                    'invoice_address': organizationPrimaryLocations.length === 1 ? organizationPrimaryLocations[0].id : null,
                };
                break;
            case 'person':
                // Get the primary location of the person
                const personPrimaryLocations = data.primary_locations?.filter((location: LocationType) => !location.deleted) || [];

                linkedItem = { 
                    'recipient': itemId,
                    'invoice_address': personPrimaryLocations.length === 1 ? personPrimaryLocations[0].id : null,
                };
                break;
            case 'location':
                linkedItem = { 
                    'invoice_address': itemId 
                };
                break;
            default:
                break;
        }

        // Add the linked item to the post data config
        if (linkedItem) {
            postDataConfig.linkedItem = linkedItem;
        }

        return postDataConfig;
    };

    return (
        <>
            {showPeriodicInvoices && (
                <InvoiceTemplateDetailList itemId={itemId} />
            )}
            <div className='detail-list'>
                <AbstractList
                    query={{
                        endpoint: 'get_invoice_list',
                        defaultParams,
                        object: 'invoice'
                    }}
                    objectName='invoice.general.object_name'
                    columns={columns}
                    filters={configuration.showSearchAndFilters !== false ? applyPageParamsToFilters(invoiceFilters, defaultParams) : undefined}
                    showSearch={configuration.showSearchAndFilters !== false}
                    showAddButton={configuration.showAddButton !== false}
                    buttonSize='small'
                    isPaginated={true}
                    postNewItem={configuration.showAddButton !== false ? postNewItem(detailPage) : undefined}
                    detailModal={<InvoiceModal />}
                    onRowClick='detailModal'
                />
            </div>
        </>
    );
};

export default InvoiceDetailList;